<template>
    <div class="relative" ref="root">
        <button
            type="button"
            :id="id"
            @click="toggle"
            :class="[
                'flex w-full items-center justify-between rounded-md bg-white px-3 py-1.5 text-left text-base outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 cursor-pointer',
                error ? 'outline-red-300 text-red-900 focus:outline-red-600 pr-10' : '',
                displayValue ? 'text-gray-900' : (showEmptyLabel ? 'text-gray-700' : 'text-gray-400')
            ]"
        >
            <span class="truncate inline-flex items-center gap-1.5">
                <svg v-if="showEmptyLabel" class="size-4 text-royal-blue-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
                </svg>
                {{ displayValue || (showEmptyLabel ? emptyLabel : placeholder) }}
            </span>
            <CalendarDaysIcon class="ml-2 size-4 shrink-0 text-gray-400" aria-hidden="true" />
        </button>

        <Teleport to="body">
        <div
            v-if="open"
            ref="popup"
            class="fixed z-100 w-72 rounded-md bg-white p-3 shadow-lg ring-1 ring-black/10"
            :style="popupStyle"
        >
            <div class="flex items-center justify-between mb-2">
                <button type="button" @click="prevMonth" class="p-1 rounded hover:bg-gray-100 cursor-pointer">
                    <ChevronLeftIcon class="size-4 text-gray-600" />
                </button>
                <div class="flex items-center gap-1">
                    <div class="relative" data-dp-dropdown>
                        <button type="button" @click="toggleDropdown('month')" class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-gray-900 hover:bg-gray-100 cursor-pointer">
                            {{ monthNames[viewMonth] }}
                            <ChevronDownIcon class="size-3 text-gray-500" />
                        </button>
                        <div v-if="dropdown === 'month'" class="absolute left-0 top-full mt-1 z-10 max-h-56 w-36 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10">
                            <button
                                v-for="(m, i) in monthNames"
                                :key="i"
                                type="button"
                                @click="pickMonth(i)"
                                :class="['block w-full text-left px-3 py-1.5 text-sm cursor-pointer', i === viewMonth ? 'bg-royal-blue-50 text-royal-blue-600 font-medium' : 'text-gray-900 hover:bg-gray-100']"
                            >{{ m }}</button>
                        </div>
                    </div>
                    <div class="relative" data-dp-dropdown>
                        <button type="button" @click="toggleDropdown('year')" class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-gray-900 hover:bg-gray-100 cursor-pointer">
                            {{ viewYear }}
                            <ChevronDownIcon class="size-3 text-gray-500" />
                        </button>
                        <div v-if="dropdown === 'year'" ref="yearList" class="absolute left-0 top-full mt-1 z-10 max-h-56 w-24 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10">
                            <button
                                v-for="y in yearRange"
                                :key="y"
                                type="button"
                                :data-year="y"
                                @click="pickYear(y)"
                                :class="['block w-full text-left px-3 py-1.5 text-sm cursor-pointer', y === viewYear ? 'bg-royal-blue-50 text-royal-blue-600 font-medium' : 'text-gray-900 hover:bg-gray-100']"
                            >{{ y }}</button>
                        </div>
                    </div>
                </div>
                <button type="button" @click="nextMonth" class="p-1 rounded hover:bg-gray-100 cursor-pointer">
                    <ChevronRightIcon class="size-4 text-gray-600" />
                </button>
            </div>

            <div class="grid grid-cols-7 gap-0.5 text-center text-xs text-gray-500 mb-1">
                <span v-for="(d, i) in weekdayNames" :key="i" class="py-1">{{ d }}</span>
            </div>

            <div class="grid grid-cols-7 gap-0.5">
                <button
                    v-for="(day, i) in days"
                    :key="i"
                    type="button"
                    :disabled="day.disabled || !day.inMonth"
                    @click="selectDay(day)"
                    :class="[
                        'h-8 w-8 rounded-full text-sm flex items-center justify-center',
                        day.disabled || !day.inMonth ? 'cursor-not-allowed opacity-30 text-gray-500' : 'cursor-pointer text-gray-900 hover:bg-gray-100',
                        day.isSelected ? '!bg-royal-blue-600 !text-white !opacity-100 hover:!bg-royal-blue-700' : '',
                        day.isToday && !day.isSelected && !day.disabled ? 'ring-1 ring-royal-blue-500' : ''
                    ]"
                >
                    {{ day.day }}
                </button>
            </div>

            <div class="mt-3 flex justify-between text-xs">
                <button type="button" @click="selectToday" class="text-royal-blue-600 hover:underline cursor-pointer">{{ $t('datepicker.today') }}</button>
                <button type="button" @click="clear" class="text-gray-500 hover:underline cursor-pointer">{{ $t('datepicker.clear') }}</button>
            </div>
        </div>
        </Teleport>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue';
import moment from 'moment';
import 'moment/locale/uk';
import 'moment/locale/pl';
import 'moment/locale/es';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/cs';
import {CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/24/outline';
import {ChevronDownIcon} from '@heroicons/vue/16/solid';

const ISO = 'YYYY-MM-DD';

export default defineComponent({
    name: 'DatePickerComponent',
    components: {CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon},
    props: {
        modelValue: {type: String as PropType<string | null>, default: null},
        placeholder: {type: String, default: ''},
        emptyLabel: {type: String, default: ''},
        error: {type: Boolean, default: false},
        min: {type: String as PropType<string | null>, default: null},
        max: {type: String as PropType<string | null>, default: null},
        id: {type: String, default: undefined},
    },
    emits: ['update:modelValue'],
    data() {
        const m = this.modelValue ? moment(this.modelValue) : null;
        const base = m && m.isValid() ? m : moment();
        return {
            open: false,
            viewMonth: base.month(),
            viewYear: base.year(),
            popupStyle: {} as Record<string, string>,
            dropdown: null as null | 'month' | 'year',
        };
    },
    computed: {
        locale(): string {
            const raw = (this.$i18n?.locale || 'en') as string;
            const map: Record<string, string> = {cz: 'cs'};
            const l = map[raw] || raw;
            return moment.locales().includes(l) ? l : 'en';
        },
        selectedMoment(): moment.Moment | null {
            if (!this.modelValue) return null;
            const m = moment(this.modelValue);
            return m.isValid() ? m : null;
        },
        displayValue(): string {
            return this.selectedMoment ? this.selectedMoment.locale(this.locale).format('LL') : '';
        },
        showEmptyLabel(): boolean {
            return !this.selectedMoment && this.modelValue === null && !!this.emptyLabel;
        },
        monthNames(): string[] {
            return moment.localeData(this.locale).months();
        },
        weekdayNames(): string[] {
            const data = moment.localeData(this.locale);
            const first = data.firstDayOfWeek();
            const short = data.weekdaysShort();
            return [...short.slice(first), ...short.slice(0, first)];
        },
        yearRange(): number[] {
            const now = moment().year();
            const years: number[] = [];
            for (let y = now - 50; y <= now + 20; y++) years.push(y);
            return years;
        },
        minMoment(): moment.Moment | null {
            if (!this.min) return null;
            const m = moment(this.min, ISO, true);
            return m.isValid() ? m.startOf('day') : null;
        },
        maxMoment(): moment.Moment | null {
            if (!this.max) return null;
            const m = moment(this.max, ISO, true);
            return m.isValid() ? m.startOf('day') : null;
        },
        days(): { day: number; date: string; inMonth: boolean; isSelected: boolean; isToday: boolean; disabled: boolean }[] {
            const first = moment({year: this.viewYear, month: this.viewMonth, day: 1});
            const firstWeekday = moment.localeData(this.locale).firstDayOfWeek();
            const offset = (first.day() - firstWeekday + 7) % 7;
            const start = first.clone().subtract(offset, 'days');
            const today = moment().startOf('day');
            const out = [];
            for (let i = 0; i < 42; i++) {
                const d = start.clone().add(i, 'days');
                const disabled =
                    (this.minMoment && d.isBefore(this.minMoment)) ||
                    (this.maxMoment && d.isAfter(this.maxMoment));
                out.push({
                    day: d.date(),
                    date: d.format(ISO),
                    inMonth: d.month() === this.viewMonth,
                    isSelected: this.selectedMoment ? d.isSame(this.selectedMoment, 'day') : false,
                    isToday: d.isSame(today, 'day'),
                    disabled: !!disabled,
                });
            }
            return out;
        },
    },
    watch: {
        modelValue(val: string | null) {
            const m = val ? moment(val) : null;
            if (m && m.isValid()) {
                this.viewMonth = m.month();
                this.viewYear = m.year();
            }
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocClick);
        window.addEventListener('resize', this.updatePosition);
        window.addEventListener('scroll', this.updatePosition, true);
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.onDocClick);
        window.removeEventListener('resize', this.updatePosition);
        window.removeEventListener('scroll', this.updatePosition, true);
    },
    methods: {
        toggle() {
            this.open = !this.open;
            if (this.open) {
                let view = this.selectedMoment ? this.selectedMoment.clone() : moment();
                if (this.minMoment && view.isBefore(this.minMoment)) view = this.minMoment.clone();
                if (this.maxMoment && view.isAfter(this.maxMoment)) view = this.maxMoment.clone();
                this.viewMonth = view.month();
                this.viewYear = view.year();
                this.$nextTick(this.updatePosition);
            }
        },
        updatePosition() {
            if (!this.open) return;
            const root = this.$refs.root as HTMLElement | undefined;
            if (!root) return;
            const rect = root.getBoundingClientRect();
            const popupWidth = 288;
            const popupHeight = 320;
            const margin = 8;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            let left = rect.left;
            if (left + popupWidth + margin > vw) left = Math.max(margin, vw - popupWidth - margin);
            let top = rect.bottom + 4;
            if (top + popupHeight + margin > vh && rect.top - popupHeight - 4 > margin) {
                top = rect.top - popupHeight - 4;
            }
            this.popupStyle = {top: `${top}px`, left: `${left}px`};
        },
        onDocClick(e: MouseEvent) {
            const root = this.$refs.root as HTMLElement | undefined;
            const popup = this.$refs.popup as HTMLElement | undefined;
            const target = e.target as Node;
            const inRoot = !!(root && root.contains(target));
            const inPopup = !!(popup && popup.contains(target));
            if (this.open && !inRoot && !inPopup) {
                this.open = false;
                this.dropdown = null;
                return;
            }
            if (this.dropdown && inPopup) {
                const el = target as HTMLElement;
                if (!el.closest('[data-dp-dropdown]')) this.dropdown = null;
            }
        },
        toggleDropdown(which: 'month' | 'year') {
            this.dropdown = this.dropdown === which ? null : which;
            if (this.dropdown === 'year') {
                this.$nextTick(() => {
                    const list = this.$refs.yearList as HTMLElement | undefined;
                    if (!list) return;
                    const sel = list.querySelector(`[data-year="${this.viewYear}"]`) as HTMLElement | null;
                    if (sel) list.scrollTop = sel.offsetTop - list.clientHeight / 2 + sel.clientHeight / 2;
                });
            }
        },
        pickMonth(i: number) {
            this.viewMonth = i;
            this.dropdown = null;
        },
        pickYear(y: number) {
            this.viewYear = y;
            this.dropdown = null;
        },
        prevMonth() {
            const m = moment({year: this.viewYear, month: this.viewMonth, day: 1}).subtract(1, 'month');
            this.viewMonth = m.month();
            this.viewYear = m.year();
        },
        nextMonth() {
            const m = moment({year: this.viewYear, month: this.viewMonth, day: 1}).add(1, 'month');
            this.viewMonth = m.month();
            this.viewYear = m.year();
        },
        selectDay(day: { date: string; disabled: boolean; inMonth: boolean }) {
            if (day.disabled || !day.inMonth) return;
            this.$emit('update:modelValue', day.date);
            this.open = false;
        },
        selectToday() {
            const today = moment().format(ISO);
            this.$emit('update:modelValue', today);
            const m = moment(today, ISO);
            this.viewMonth = m.month();
            this.viewYear = m.year();
            this.open = false;
        },
        clear() {
            this.$emit('update:modelValue', null);
            this.open = false;
        },
    },
});
</script>
